/**
 * Validação e normalização da naturalidade das pessoas vinculadas (GYR-1883).
 *
 * O CPE recusa o envio quando a naturalidade não está no formato "Cidade/UF"
 * (`generatePersonJson` lança "Naturalidade inválida. Deve ser no formato
 * Cidade/UF"). O dado é digitado à mão no app e no dashboard, então o erro só
 * aparecia no envio, longe de quem preencheu. Estas regras rodam no campo (front),
 * no salvamento (core-graphql) e antes do envio (parse-server).
 *
 * Cópia da regra do servidor (parse-server e core-graphql: services/badesul/
 * naturality.js) para validar no campo, antes de salvar. O servidor continua
 * sendo a barreira; repos separados, sem pacote compartilhado: ao mudar a regra
 * lá, mude aqui (e o JSON de municípios).
 *
 * Tabela: `municipalities-by-uf.json`, gerada da API de localidades do IBGE
 * (5.571 municípios, 27 UFs). `legacyNames` guarda grafias antigas que a tabela
 * de municípios do BADESUL ainda usa (ex.: "Parati", "Embu") e que já podem
 * estar gravadas em pessoas existentes; elas passam, e saem com a grafia do
 * IBGE quando existir equivalente exato.
 */
import data from './municipalities-by-uf.json';

const { ufs, municipalities, legacyNames } = data;

const NATURALITY_EXAMPLE = 'Porto Alegre/RS';

// Teto defensivo: o maior município tem 32 caracteres. Um campo de texto livre
// pode chegar com qualquer tamanho, e a regex abaixo não deve varrer megabytes.
const MAX_LENGTH = 120;

/**
 * Chave de comparação: sem acento, caixa alta, espaços colapsados.
 * @param {string} value
 * @returns {string}
 */
const normalize = (value) => value
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/\s+/g, ' ')
  .toUpperCase()
  .trim();

const UF_SET = new Set(ufs);

/** @type {Map<string, string>} "<UF>|<NOME NORMALIZADO>" → nome canônico */
const CITY_BY_UF_AND_KEY = new Map();
for (const uf of ufs) {
  for (const name of municipalities[uf]) {
    CITY_BY_UF_AND_KEY.set(`${uf}|${normalize(name)}`, name);
  }
}
for (const uf of Object.keys(legacyNames)) {
  for (const name of legacyNames[uf]) {
    const key = `${uf}|${normalize(name)}`;
    if (!CITY_BY_UF_AND_KEY.has(key)) {
      CITY_BY_UF_AND_KEY.set(key, name);
    }
  }
}

// "Cidade/UF", "Cidade - UF", "Cidade, UF", "Cidade-UF". O nome da cidade é
// preguiçoso e a UF ancora no fim: "Embu-Guaçu/SP" quebra no "/", não no "-".
const SPLIT_REGEX = /^(.+?)\s*[/,\-–—]\s*([A-Za-z]{2})$/;

/**
 * @param {unknown} personName
 * @returns {string}
 */
const formatPersonName = (personName) => {
  if (typeof personName !== 'string' || !personName.trim()) {
    return '';
  }
  return personName
    .trim()
    .toLowerCase()
    .replace(/(^|\s)(\S)/g, (_, space, letter) => space + letter.toUpperCase());
};

/**
 * @param {string} personName
 * @param {string} detail
 * @returns {string}
 */
const buildMessage = (personName, detail) => {
  const subject = personName
    ? `Naturalidade de ${personName} inválida`
    : 'Naturalidade inválida';
  return `${subject}${detail}. Informe no formato Cidade/UF, ex.: ${NATURALITY_EXAMPLE}.`;
};

/**
 * @typedef {{ ok: true, value: string, city: string, uf: string }
 *   | { ok: false, reason: 'type' | 'empty' | 'format' | 'uf' | 'city', message: string }
 * } NaturalityResult
 */

/**
 * Valida a naturalidade e a devolve no formato canônico `Cidade/UF`.
 * Aceita as variações comuns de digitação (RN8.1) e as normaliza.
 *
 * @param {unknown} raw valor vindo do front (qualquer JSON)
 * @param {unknown} [personName] nome da pessoa, para a mensagem de erro
 * @returns {NaturalityResult}
 */
const validateNaturality = (raw, personName) => {
  const name = formatPersonName(personName);

  if (typeof raw !== 'string') {
    return { ok: false, reason: 'type', message: buildMessage(name, '') };
  }

  const text = raw.trim();
  if (!text) {
    return { ok: false, reason: 'empty', message: buildMessage(name, '') };
  }
  if (text.length > MAX_LENGTH) {
    return { ok: false, reason: 'format', message: buildMessage(name, '') };
  }

  const match = SPLIT_REGEX.exec(text);
  if (!match) {
    return { ok: false, reason: 'format', message: buildMessage(name, '') };
  }

  const cityTyped = match[1].trim();
  const uf = match[2].toUpperCase();

  if (!UF_SET.has(uf)) {
    return {
      ok: false,
      reason: 'uf',
      message: buildMessage(name, `: a UF "${uf}" não existe`),
    };
  }

  const city = CITY_BY_UF_AND_KEY.get(`${uf}|${normalize(cityTyped)}`);
  if (!city) {
    return {
      ok: false,
      reason: 'city',
      message: buildMessage(name, `: "${cityTyped}" não é um município de ${uf}`),
    };
  }

  return {
    ok: true, value: `${city}/${uf}`, city, uf,
  };
};

/**
 * Cidades de uma UF, na grafia do IBGE, para o seletor do front.
 * @param {string} uf
 * @returns {string[]}
 */
const listCitiesByUf = (uf) => [...(municipalities[String(uf).toUpperCase()] || [])];

export {
  NATURALITY_EXAMPLE,
  ufs as UFS,
  validateNaturality,
  listCitiesByUf,
  normalize as normalizeText,
};
